// View-model derivation for the open-positions card view. Mirrors the Open
// Positions table in PositionTracker so the card and table agree on every
// figure (avg / cost / mark / P&L / cash-out / if-win).

import type { PolyFeeSchedule, PolyRelayPosition } from '../../lib/polymarket/relay';
import type { PositionWithPnl } from '../../hooks/ui/usePositionPnl';
import { truncateTicker } from '../../lib/kalshiTicker/describeMarket';
import {
  describePolyMarketCached,
  describePolyOrderCached,
  resolvePolyCloseTarget,
} from '../../lib/polymarket/describeOrder';
import { polyMarketTerms, polyOutcomeLabel } from '../../lib/polymarket/marketTerms';
import { americanOdds } from '../../views/ValueDashboard/tradeModalBook';
import { estimatePolyOrderFeeCents } from '../../lib/pricing/polyFees';
import { polyTradeEconomics } from '../../lib/pricing/polyEconomics';
import { titleParts } from './cardFormat';
import { useMarketStore } from '../../stores/marketStore';
import type { TradingVenue } from '../../lib/venues';
import type { UsPositionRow } from '../../lib/polymarketUs/types';
import { presentUsPosition } from '../../lib/polymarketUs/portfolioModel';
import { formatUsDecimal, formatUsUsd } from '../../lib/polymarketUs/format';
import { usReason } from '../../lib/polymarketUs/reason';
import { usMarketLabel, type UsMarketTeams } from '../../lib/polymarketUs/marketLabel';
import { usHoldingPinKey } from '../../views/PositionTracker/pinIdentity';
import { usPositionEconomics, type UsPositionMarket } from '../../lib/polymarketUs/positionEconomics';

export interface PositionCardVM {
  /** Fee-exclusive remaining basis, for surfaces whose Value column uses that convention. */
  entryBasisDollars?: number | null;
  key: string;
  /** Market traded volume from the live frame (null pre-frame /
   *  non-streamed markets). Decorated by the view, not the builders. */
  volume?: number | null;
  /** #980 — market family still loading; set by Position Tracker only. */
  familyPending?: boolean;
  venue: TradingVenue;
  ticker: string;
  title: string;
  subtitle: string;
  eventStartLabel: string | null;
  /** Most recent fill in the aggregate; null means the source cannot prove it. */
  lastFillAt: string | null;
  side: 'yes' | 'no';
  /** Short code for the standalone "(NYC) YES" side badge. */
  team: string | null;
  /** Token to highlight inside `title` — the club NAME on a feed-named
   *  matchup, where `team` is still the code. See cardFormat.titleParts. */
  titleTeam: string | null;
  /** #964 — an NFL ladder prop's stat, shown as its own label beside (or
   *  under) the one-line side badge (see cardFormat.titleParts). Absent
   *  otherwise. */
  marketType?: string | null;
  badgeThreshold?: string;
  /** Contracts still HELD — the post-sale remainder. Every live figure below
   *  (cost / mark / P&L / cash-out / if-win) is anchored to this, matching the
   *  Open Positions table. Use originalQty for the full-position anchor. */
  qty: number;
  /** Contracts on resting orders that close this position when they fill —
   *  held-side sells and, on Kalshi, other-side buys (see workingCloses; #949).
   *  Still HELD until they fill. */
  restingCloseQty: number;
  /** ── Full-position anchor (#91) ────────────────────────────────────────
   *  Lifetime bought / sold, stable across partial sales. A trader who opened
   *  150,000 and sold down to 6,297 must still see the 150,000 they entered;
   *  reading qty as the original is the exact defect #91 was filed for. Null
   *  when the source can't prove them (raw venue aggregates carry no lifetime
   *  sums) — render a dash, never a zero, and never fall back to qty.
   *
   *  Semantics are LIFETIME-BUCKET, not initial-order: a buy → partial sell →
   *  rebuy grows originalQty to everything ever bought in the (ticker, side)
   *  bucket, and originalCostDollars blends both price levels. Whether the
   *  strip should keep saying "original" for a rebuilt position is an open
   *  label decision (Brendan, #173 review) — pinned by the rebuy test in
   *  positionCardModel.test.ts so it can't shift silently. */
  originalQty: number | null;
  soldQty: number | null;
  /** Original entry basis in dollars, from the ledger's lifetime buy cost —
   *  NOT avg × originalQty, which re-multiplies a rounded avg (#91 rules that
   *  out explicitly). Fee-exclusive, unlike costDollars below, which is the
   *  held share's entry + fees; the two are different conventions on purpose
   *  and must be labeled distinctly wherever both appear. */
  originalCostDollars: number | null;
  /** Gross payout the ORIGINAL position would have returned on a win, net of
   *  its entry basis (100¢ × originalQty − originalCostDollars). */
  originalIfWinDollars: number | null;
  /** Null on a Polymarket US row until #1199 (average cost); renders as "—". */
  avgCents: number | null;
  avgOdds: string | null;
  feesCents: number | null;
  /** Entry cost + fees, in dollars. Null on a Polymarket US row until #1199. */
  costDollars: number | null;
  /** Current side-native mark. Null when Hydra hasn't priced it. */
  nowCents: number | null;
  nowOdds: string | null;
  pnlDollars: number | null;
  cashOutDollars: number | null;
  ifWinDollars: number | null;
  /** Gross settlement payout on a win (qty × $1). */
  grossPayoutDollars: number | null;
  impliedOdds: string | null;
  /** Kalshi positions can be closed/edited; poly can't (yet). */
  editable: boolean;
  /** #1226 — a Polymarket US holding's size and fees as the exact decimal
   *  text G1's ledger reports (`qty` is an approximation kept for sort
   *  fallbacks, never displayed). Present on US rows only; "—" when G1
   *  cannot size the holding. */
  qtyText?: string;
  feesText?: string;
  /** #1226 — the US row's account, partly-known word and Close verdict. */
  us?: UsPositionFacts;
}

/** #1226 — what a Polymarket US holding row carries beside the shared
 *  fields. Primitives only, so the row stays comparable by value. */
export interface UsPositionFacts {
  economicsTitle?: string;
  accountId: string;
  marketSlug: string;
  outcome: 'yes' | 'no';
  /** The account's name beside the venue chip with two or more own-view
   *  accounts; null with one. */
  accountLabel: string | null;
  /** One amber word beside the size (`Size unknown` / `Partly known`), with
   *  the ledger's reasons as its hover; null when the holding is complete. */
  partialText: string | null;
  partialTitle: string | null;
  /** The US Close: a sell of `closeQuantity` when offered; otherwise greyed
   *  with `closeReason` in its title; neither = no Close on this row. */
  closeQuantity: string | null;
  closeReason: string | null;
  /** A read or action on the account is in flight: Close waits. */
  busy: boolean;
  /** Recorded execution fees as the hover explains them. */
  feesTitle: string;
  /** The one-line table label in the K/P shape (`BAL @ BUF — NFL — 09-28`); the slug is the hover. */
  marketLabel: string;
}

/** Full-position anchor shared by both venue builders (#91). Kept in one place
 *  so Kalshi and Poly cards can't drift on what "original" means — the
 *  acceptance criteria require PositionCard and its pinned/detail consumers to
 *  agree. Withholds (null) rather than coercing: a missing lifetime sum means
 *  the source is a raw venue aggregate, and showing 0 contracts bought on a
 *  live position is a worse lie than a dash. */
export function fullPositionAnchor(src: {
  totalBoughtCount?: number;
  totalSoldCount?: number;
  totalBuyCostCents?: number;
}): Pick<
  PositionCardVM,
  'originalQty' | 'soldQty' | 'originalCostDollars' | 'originalIfWinDollars'
> {
  const bought =
    typeof src.totalBoughtCount === 'number' && src.totalBoughtCount > 0
      ? src.totalBoughtCount
      : null;
  const buyCostCents =
    typeof src.totalBuyCostCents === 'number' ? src.totalBuyCostCents : null;
  return {
    originalQty: bought,
    soldQty: typeof src.totalSoldCount === 'number' ? src.totalSoldCount : null,
    originalCostDollars: buyCostCents != null ? buyCostCents / 100 : null,
    originalIfWinDollars:
      bought != null && buyCostCents != null
        ? (100 * bought - buyCostCents) / 100
        : null,
  };
}

export function buildKalshiPositionVM(
  pos: PositionWithPnl,
  restingCloseQty: number,
  eventStartLabel: string | null = null,
  /** The game's resolved `YYYY-MM-DD` for the subtitle date; null keeps the ticker's. */
  gameDate: string | null = null,
): PositionCardVM {
  const { title, subtitle, team, titleTeam, marketType, badgeThreshold } = titleParts(pos.ticker, pos.side, {
    gameDate,
  });
  return {
    key: `${pos.ticker}-${pos.side}`,
    venue: 'kalshi',
    ticker: pos.ticker,
    title,
    subtitle,
    eventStartLabel,
    lastFillAt: pos.lastFillAt ?? null,
    side: pos.side,
    team,
    titleTeam,
    marketType,
    ...(badgeThreshold ? { badgeThreshold } : {}),
    qty: pos.position,
    restingCloseQty,
    ...fullPositionAnchor(pos),
    avgCents: pos.averagePrice,
    // Entry odds from the EXACT basis ratio, not the whole-cent-rounded avg
    // — rounding first shifts the line (a 63.62¢ VWAP must read -174.9, not
    // 64¢'s -177.8). Same unrounded-ratio treatment as the table's implied
    // entry (PositionTracker ~3629). Rounded avg only when no basis (per-
    // sonal-key positions without a relay ledger).
    avgOdds: americanOdds(
      pos.costBasisCents != null && pos.position > 0
        ? pos.costBasisCents / pos.position
        : pos.averagePrice,
    ),
    feesCents: pos.feesPaidCents ?? null,
    costDollars: pos.totalCostCents / 100,
    nowCents: pos.currentPrice,
    // Unrounded: centsToAmericanOdds handles decimals natively, and rounding
    // a sub-cent mark (0.1¢) to 0 nulled the odds for real longshot marks.
    nowOdds: pos.currentPrice != null ? americanOdds(pos.currentPrice) : null,
    pnlDollars: pos.unrealizedPnl != null ? pos.unrealizedPnl / 100 : null,
    cashOutDollars: pos.cashOutCents != null ? pos.cashOutCents / 100 : null,
    ifWinDollars: pos.ifWinCents / 100,
    grossPayoutDollars: pos.position,
    impliedOdds:
      pos.position > 0 ? americanOdds(pos.totalCostCents / pos.position) : null,
    editable: true,
  };
}

/** Bet label for a poly POSITION's side badge — the same "(betTeam) SIDE"
 *  convention the poly ORDER cards already render (orderCardModel ~330) and
 *  Kalshi's describeMarket documents ("(BAL) NO"): the label names the
 *  market's YES outcome, the badge side is the ledger side against it.
 *
 *  Positions carry a CONDITION ID, so this routes (conditionId, side) →
 *  tokenId → the cached token description. The description's orientation is
 *  book-relative and the moneyline sweep checks the away book first, so a
 *  cross-listed token can resolve normalized to the OTHER team — when the
 *  sides disagree, translate back through the "AWY @ HOM" matchup rather
 *  than pair betTeam with a side it doesn't describe; anything untranslatable
 *  drops the label (plain YES/NO chip) instead of risking a wrong badge.
 *  Placeholder outcomes ('—', literal Yes/No) also drop.
 *
 *  Exported for the game-local position snapshot (#280), which needs the same
 *  refuse-rather-than-guess orientation rule on the trading card. Copying it
 *  would give one position two orientations depending on which surface you
 *  read. */
export function polyPositionBetLabel(
  conditionId: string,
  side: 'yes' | 'no',
): string | null {
  const target = resolvePolyCloseTarget(conditionId, side);
  const desc = target ? describePolyOrderCached(target.tokenId) : null;
  if (!desc) return null;
  const label = polyOutcomeLabel(desc.betTeam);
  if (!label) return null;
  if (desc.side === side) return label;
  const codes = desc.matchup.split(' @ ').map((s) => s.trim());
  if (codes.length !== 2) return null;
  if (label === codes[0]) return codes[1];
  if (label === codes[1]) return codes[0];
  return null;
}

export function buildPolyPositionVM(
  pos: PolyRelayPosition,
  eventStartLabel: string | null = null,
  /** Live side-native mark (polyPositionLiveData — the table row's Now
   *  source). Passed by the view like buildKalshiOrderVM's live inputs;
   *  null when the market isn't in the live store (feed down / settled),
   *  in which case every live-derived field stays null — dashes, not $0. */
  nowCents: number | null = null,
  /** Relay/Gamma condition-id fee schedule. Undefined/loading and null/failed
   *  both withhold liquidation P&L; a known fee-free schedule carries an
   *  explicit zero rate and still produces a real cash-out value. */
  feeSchedule: PolyFeeSchedule | null | undefined = undefined,
): PositionCardVM {
  const { title, subtitle, team, titleTeam } = titleParts(pos.ticker, pos.side);
  // pos.ticker is the market CONDITION ID — hex titleParts can never parse,
  // so without help the card title IS the truncated hex. The market-level
  // poly lookup (live conditionId index + labels persisted from any prior
  // token resolution) recovers the matchup even after the market ages out
  // of the feed. Orientation comes from the token-level description via
  // polyPositionBetLabel (Kalshi-parity "(betTeam) SIDE" chip); the ledger
  // row's side stays authoritative for keys and the order-panel opener.
  const mkt = describePolyMarketCached(pos.ticker);
  // #685 — for a game the shared index knows (hydra's canonical away/home,
  // named by canonicalTeamNames), the matchup is spelled the way the dashboard
  // row spells it, not the way this card's cache happens to have frozen it. `describePolyMarketCached` serves `mkt.title` from a
  // localStorage entry that never re-resolves, so a club renamed after the
  // first resolution stayed wrong on this card forever — the one surface that
  // could show a genuinely OLD NAME rather than a stale code.
  //
  // Only the matchup half is replaced: `variant` carries the poly market's own
  // qualifier (": Over 2.5"), which the index knows nothing about. A fixture
  // with no indexed game — poly-only, never listed on Kalshi — keeps the
  // venue's own title, because inventing a name is worse than an old one.
  const named = mkt?.pmTicker
    ? useMarketStore.getState().getGameMatchup(mkt.pmTicker)
    : null;
  // A standalone market title (e.g. "Spread: Georgia Tech (-5.5)") contains
  // the held contract's terms. Canonical matchup naming must not replace it
  // without retaining those terms in the subtitle. The order card reads the
  // same helper (#1450), so the two cards agree about one market.
  const { gameTitle, terms: variant } = polyMarketTerms(
    mkt?.matchup ?? (title || truncateTicker(pos.ticker)),
    named,
  );
  const betLabel = polyPositionBetLabel(pos.ticker, pos.side);
  // Economics mirror the table's poly row (PositionTracker ~3223-3236):
  // exact relay basis (avg×qty only as a fallback for stale cached payloads
  // — whole-cent avg re-multiplied drifts, the I-035 class), cost = entry +
  // fees, Cash Out = gross live value − estimated taker close fee,
  // P&L = Cash Out − Cost, If W = 100¢×qty − Cost. Missing schedule stays
  // unknown; it must never silently turn into a fee-free liquidation.
  const costCents = pos.costBasisCents ?? pos.avgEntryPriceCents * pos.netCount;
  const totalCostCents = costCents + (pos.feesPaidCents ?? 0);
  // Entry juice per the client accounting convention (polyEconomics): odds
  // of laying the fee-inclusive cost to clear the fee-free winnings — the
  // same trio the closed-trade card shows, so a position's juice doesn't
  // restate at settlement. Unknown fee evidence keeps it null (dash).
  const entryEconomics = polyTradeEconomics({
    qty: pos.netCount,
    totalInDollars: pos.feesPaidCents == null ? null : totalCostCents / 100,
    feesDollars: pos.feesPaidCents == null ? null : pos.feesPaidCents / 100,
  });
  const closingFeeCents = nowCents != null
    ? estimatePolyOrderFeeCents({
        priceCents: nowCents,
        count: pos.netCount,
        role: 'taker',
        schedule: feeSchedule,
      })
    : null;
  const cashOutCents =
    nowCents != null && closingFeeCents != null
      ? nowCents * pos.netCount - closingFeeCents
      : null;
  const pnlCents = cashOutCents != null ? cashOutCents - totalCostCents : null;
  return {
    key: `poly-pos-${pos.ticker}-${pos.side}`,
    venue: 'polymarket',
    ticker: pos.ticker,
    title: gameTitle,
    // Market variant leads the subtitle — it's the only field distinguishing
    // a total/spread card from the same game's moneyline cards, so it must
    // survive ahead of the truncatable tail.
    subtitle: mkt
      ? [variant, mkt.sport.toUpperCase(), truncateTicker(pos.ticker)]
          .filter(Boolean)
          .join(' · ')
      : subtitle,
    eventStartLabel,
    lastFillAt: pos.lastFillAt ?? null,
    side: pos.side,
    team: betLabel ?? team,
    titleTeam: betLabel ?? titleTeam,
    qty: pos.netCount,
    restingCloseQty: 0,
    ...fullPositionAnchor(pos),
    avgCents: pos.avgEntryPriceCents,
    // Exact basis ratio, not the whole-cent avg — see the Kalshi builder.
    avgOdds: americanOdds(
      pos.costBasisCents != null && pos.netCount > 0
        ? pos.costBasisCents / pos.netCount
        : pos.avgEntryPriceCents,
    ),
    feesCents: pos.feesPaidCents ?? null,
    costDollars: totalCostCents / 100,
    nowCents,
    // Unrounded — same longshot rationale as the Kalshi builder above.
    nowOdds: nowCents != null ? americanOdds(nowCents) : null,
    pnlDollars: pnlCents != null ? pnlCents / 100 : null,
    cashOutDollars: cashOutCents != null ? cashOutCents / 100 : null,
    ifWinDollars: (100 * pos.netCount - totalCostCents) / 100,
    grossPayoutDollars: pos.netCount,
    impliedOdds: entryEconomics.juiceOdds == null ? null : entryEconomics.juice,
    editable: false,
  };
}

/** What the Polymarket US builder needs from the row's account reader. */
export interface UsPositionCtx {
  market?: UsPositionMarket | null;
  nowMs?: number;
  accountId: string;
  accountLabel: string | null;
  eventStartLabel: string | null;
  /** The market's sport, for the subtitle; null until classified. */
  sport: string | null;
  /** The matched board row's team codes for the title (`usGameTeams`); null = none. */
  teams?: UsMarketTeams | null;
  /** The reader's Close verdict for this holding (`usTrackerFeed.usCloseStateFor`). */
  closeState: { quantity: string } | { reason: string } | null;
  busy: boolean;
}

/** `usReason` names a code the vocabulary knows; its fallback already carries
 * the code, so nothing is bracketed twice. */
const knownUsReason = (code: string): string | null => {
  const text = usReason(code);
  return text.endsWith(`(${code})`) ? null : text;
};
/** A ledger reason in plain words with its code; one the vocabulary does not
 * name is its code alone, never "unavailable". */
const plainUsReason = (code: string) => {
  const text = knownUsReason(code);
  return text ? `${text} [${code}]` : code;
};

/** The one word a partly known holding carries beside its size, and its
 *  hover: what G1 could not establish, in plain words with the ledger's
 *  codes. Null when the holding is complete. */
export function usHoldingPartial(row: UsPositionRow): { text: string; title: string } | null {
  const view = presentUsPosition(row);
  if (view.complete) return null;
  const sizeUnknown = row.heldQuantity === null;
  const reasons = view.reasons.length ? view.reasons.map(plainUsReason).join('; ') : 'G1 could not establish this holding';
  return {
    text: sizeUnknown ? 'Size unknown' : 'Partly known',
    title: `${sizeUnknown ? 'G1 cannot size this holding' : 'G1 can only partly account for this holding'}; it is not counted in totals. ${reasons}`,
  };
}

/**
 * #1226 — a Polymarket US holding as a row of the shared Open Positions list.
 * #1199 supplies complete-ledger basis and exact current-market economics;
 * missing inputs remain null and render as "—". Size is exact decimal text (a dash
 * when G1 cannot size it); identity is the account plus the exact market and
 * outcome (`usHoldingPinKey`), which is also the row's pin key.
 */
export function buildUsPositionVM(row: UsPositionRow, ctx: UsPositionCtx): PositionCardVM {
  const economics = usPositionEconomics(row, ctx.market, ctx.nowMs);
  const view = presentUsPosition(row);
  const held = row.heldQuantity === null ? null : Number(row.heldQuantity);
  const partial = usHoldingPartial(row);
  const close = ctx.closeState;
  const name = usMarketLabel(row.target, { sport: ctx.sport, teams: ctx.teams });
  return {
    key: usHoldingPinKey(ctx.accountId, row.target.marketSlug, row.target.outcome),
    venue: 'polymarket_us',
    ticker: row.target.marketSlug,
    title: name.title,
    subtitle: [name.sport, name.date, name.tail].filter(Boolean).join(' · '),
    eventStartLabel: ctx.eventStartLabel,
    lastFillAt: row.lastFill?.at ?? null,
    side: row.target.outcome,
    team: null,
    titleTeam: null,
    qty: held != null && Number.isFinite(held) ? held : 0,
    restingCloseQty: 0,
    originalQty: null,
    soldQty: null,
    originalCostDollars: null,
    originalIfWinDollars: null,
    avgCents: economics.averageCents,
    avgOdds: economics.averageCents == null ? null : americanOdds(economics.averageCents),
    feesCents: economics.openingFeesDollars == null ? null : economics.openingFeesDollars * 100,
    costDollars: economics.costDollars,
    entryBasisDollars: economics.entryBasisDollars,
    nowCents: economics.nowCents,
    nowOdds: economics.nowCents == null ? null : americanOdds(economics.nowCents),
    pnlDollars: economics.pnlDollars,
    cashOutDollars: economics.cashOutDollars,
    ifWinDollars: economics.ifWinDollars,
    grossPayoutDollars: economics.grossPayoutDollars,
    impliedOdds: null,
    editable: false,
    qtyText: row.heldQuantity === null ? '—' : formatUsDecimal(view.held),
    feesText: (row.accounting ? row.accounting.openingFeesUsd : row.feesUsd) == null ? '—'
      : formatUsUsd((row.accounting ? row.accounting.openingFeesUsd : row.feesUsd)!),
    us: {
      accountId: ctx.accountId,
      marketSlug: row.target.marketSlug,
      outcome: row.target.outcome,
      accountLabel: ctx.accountLabel,
      partialText: partial?.text ?? null,
      partialTitle: partial?.title ?? null,
      closeQuantity: close && 'quantity' in close ? close.quantity : null,
      closeReason: close && 'reason' in close ? close.reason : null,
      busy: ctx.busy,
      feesTitle: row.accounting ? 'Recorded entry fees allocated to the remaining holding' : 'Recorded execution fees on this holding',
      economicsTitle: economics.reason ?? 'Unrealized estimate uses the current midpoint, recorded entry cost and entry fees, and an estimated taker closing fee.',
      marketLabel: name.label,
    },
  };
}

export interface FullPositionSummary {
  originalQty: number;
  soldQty: number;
  /** Contracts still held — vm.qty, restated so consumers don't mix anchors. */
  heldQty: number;
  /** Fee-EXCLUSIVE original entry basis. Deliberately a different convention
   *  from the card's `costDollars` (held share, entry + fees) — label them
   *  distinctly, never side by side unlabeled. */
  originalCostDollars: number | null;
  originalIfWinDollars: number | null;
}

/** The one definition of "partially sold" (#91), shared by PositionCard and the
 *  pinned rows so the two surfaces can't disagree about when a full-position
 *  anchor exists or what it says.
 *
 *  Null unless the position was genuinely sold down: an untouched position has
 *  nothing to disambiguate, and Brendan's 2026-07-22 note is explicit that the
 *  common single-order case must NOT grow a residual-style `6,297/10,053`
 *  presentation. So unsold cards render exactly as before, and only a sold-down
 *  position gains the labeled original/sold context.
 *
 *  Also null when the lifetime sums are absent (raw venue aggregates, the org
 *  rollup) — withhold, never substitute qty for the original. */
/** Reconciliation tolerance for the strip gate below. Counts are double-
 *  precision (poly fractional fills), so exact equality would flake on float
 *  dust — same epsilon settlementExport uses for its orphan-sell detection. */
const FULL_POSITION_RECONCILE_EPS = 1e-6;

export function fullPositionSummary(
  vm: Pick<
    PositionCardVM,
    'qty' | 'originalQty' | 'soldQty' | 'originalCostDollars' | 'originalIfWinDollars'
  >,
): FullPositionSummary | null {
  const { originalQty, soldQty } = vm;
  if (originalQty == null || soldQty == null) return null;
  // Requires BOTH a recorded sale and a real shortfall vs the original. Either
  // alone is a false positive: settlement/expiry can shrink held without a
  // sale, and a re-bought position can carry an old sold count while sitting
  // back at full size.
  if (soldQty <= 0 || originalQty <= vm.qty) return null;
  // Reconciliation gate (Brendan, #173 review): the lifetime sums count EVERY
  // fill, including orphan sells the running engine skips — so a bucket can
  // carry a sold count its recorded buys don't explain. Unless
  // original − sold ≈ held, the three figures are not one position's story,
  // and the strip would print impossible arithmetic (original 100 · sold 150
  // · held 20). Withhold instead; the held-share stats above stay correct.
  if (Math.abs(originalQty - soldQty - vm.qty) > FULL_POSITION_RECONCILE_EPS) {
    return null;
  }
  return {
    originalQty,
    soldQty,
    heldQty: vm.qty,
    originalCostDollars: vm.originalCostDollars,
    originalIfWinDollars: vm.originalIfWinDollars,
  };
}

/** One-line full-position text for width-constrained surfaces (#91) — the
 *  pinned strip's qty cell is 4rem, so the labeled figures the card shows
 *  inline ride a tooltip there instead. Shared with the card's line so the two
 *  can never quote different numbers. Dollar figures are omitted rather than
 *  dashed: a tooltip is scanned, not aligned. */
export function fullPositionTooltip(full: FullPositionSummary): string {
  const parts = [
    `bought ${full.originalQty.toLocaleString()}`,
    `sold ${full.soldQty.toLocaleString()}`,
    `held ${full.heldQty.toLocaleString()}`,
  ];
  if (full.originalCostDollars != null) {
    parts.push(`buy cost $${full.originalCostDollars.toFixed(2)} (before fees)`);
  }
  if (full.originalIfWinDollars != null) {
    parts.push(`if all won $${full.originalIfWinDollars.toFixed(2)} (before fees)`);
  }
  return parts.join(' · ');
}

/** Mark-price tint — mirrors PositionTracker's nowColorClass buckets so the
 *  card's "now" figure reads the same red→green as the table's column. */
export function nowTone(cents: number | null | undefined): string {
  if (cents == null) return 'text-muted-foreground';
  if (cents <= 15) return 'text-red-500';
  if (cents <= 25) return 'text-red-400';
  if (cents <= 35) return 'text-red-300';
  if (cents <= 45) return 'text-orange-400';
  if (cents <= 55) return 'text-yellow-400';
  if (cents <= 65) return 'text-[#9aa00d]';
  if (cents <= 75) return 'text-green-300';
  if (cents <= 85) return 'text-green-400';
  return 'text-green-500';
}
